// Preauthorization letter viewer — narrative + three-layer sources
// (carrier policy / Texas Insurance Code / records) + DOCX/PDF export.
//
// Implements: SPEC §4.2.3 draft viewer — distinguishes the three source layers,
// surfaces the "carrier policy not loaded" plain-language note when the declared
// carrier has no matching fixture policy, and shows the appeal-mode rebuttal.

import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { ApiError, preauth } from "../lib/api";
import { useAsync } from "../hooks/useAsync";
import { pushToast } from "../components/Toast";

export function PreAuthDraftDetailPage(): JSX.Element {
  const params = useParams<{ draftId: string }>();
  const draftId = Number(params.draftId);
  const { data, loading, error } = useAsync(() => preauth.getDraft(draftId), [draftId]);
  const [exporting, setExporting] = useState<"docx" | "pdf" | null>(null);

  if (Number.isNaN(draftId)) return <p className="text-sm text-red-700">Invalid draft id.</p>;
  if (loading) return <p className="text-sm text-slate-500">Loading letter…</p>;
  if (error) return <p className="text-sm text-red-700">{error.message}</p>;
  if (!data) return <p className="text-sm text-slate-500">No letter.</p>;

  const onExport = async (fmt: "docx" | "pdf"): Promise<void> => {
    setExporting(fmt);
    try {
      const blob =
        fmt === "docx" ? await preauth.exportDocx(draftId) : await preauth.exportPdf(draftId);
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = `preauth-draft-${draftId}.${fmt}`;
      window.document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      pushToast("success", `Exported letter as ${fmt.toUpperCase()}`);
    } catch (err) {
      pushToast("error", err instanceof ApiError ? err.message : `Export failed (${fmt})`);
    } finally {
      setExporting(null);
    }
  };

  const carrier = data.sources.carrier_policy;

  return (
    <div>
      <nav className="mb-2 text-sm text-slate-500">
        <Link to={`/cases/${data.case_id}`} className="hover:text-brand-700">
          Case #{data.case_id}
        </Link>
        <span aria-hidden> / </span>
        <span>Preauth letter #{draftId}</span>
      </nav>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Preauthorization letter #{draftId}
          </h1>
          <p className="text-sm text-slate-500">
            {data.treatment_name} · {data.goal} · {data.workflow_mode} · {data.authorship_mode} ·{" "}
            citation status:{" "}
            {data.citation_status === "verified" ? (
              <span className="badge-green">verified</span>
            ) : (
              <span className="badge-red">{data.citation_status}</span>
            )}{" "}
            · generated {new Date(data.created_at).toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-primary"
            disabled={exporting !== null}
            onClick={() => onExport("docx")}
          >
            {exporting === "docx" ? "Exporting…" : "Export DOCX"}
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={exporting !== null}
            onClick={() => onExport("pdf")}
          >
            {exporting === "pdf" ? "Exporting…" : "Export PDF"}
          </button>
        </div>
      </div>

      {/* Carrier policy layer — or the plain "not loaded" fallback (AC). */}
      <section className="card mb-4 p-5">
        <h2 className="mb-2 text-lg font-medium text-slate-800">Carrier policy</h2>
        {carrier.available && carrier.policies.length > 0 ? (
          <ul role="list" className="space-y-1 text-sm">
            {carrier.policies.map((p, i) => (
              <li key={i}>
                <span className="font-medium text-slate-900">{p.policy_number}</span>
                {" — "}
                <span className="text-slate-700">{p.title}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="max-w-prose-narrow text-sm text-amber-700">
            {data.carrier_policy_note ||
              "No carrier policy was loaded for this carrier/treatment. The letter was built from your plan type, the Texas Insurance Code, and your records, without carrier-specific criteria."}
          </p>
        )}
      </section>

      {/* Empirical carrier layer (SPEC-ADDENDUM §A9 / PA-12): the de-identified
          denial patterns this letter pre-empted in expect-denial mode. */}
      {data.sources.anticipated_denials && data.sources.anticipated_denials.length > 0 && (
        <section className="card mb-4 p-5">
          <h2 className="mb-2 text-lg font-medium text-slate-800">
            Anticipated denials ({data.sources.anticipated_denials.length})
          </h2>
          <p className="mb-2 max-w-prose-narrow text-sm text-slate-600">
            Based on this carrier's observed denial patterns for comparable requests, the letter
            pre-empts the following likely objections.
          </p>
          <ul role="list" className="space-y-1 text-sm">
            {data.sources.anticipated_denials.map((d, i) => (
              <li key={i}>
                <span className="font-medium text-amber-700">
                  {d.denial_reason.replace(/_/g, " ")}
                </span>
                <span className="text-slate-500"> · {d.denial_code}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="card mb-4 p-5">
        <h2 className="mb-2 text-lg font-medium text-slate-800">Procedural anchor</h2>
        <div className="text-sm">
          <div>
            <span className="font-medium text-slate-900">{data.procedural_anchor.citation}</span>
            {" — "}
            <span className="text-slate-700">{data.procedural_anchor.title}</span>
          </div>
          {data.procedural_anchor.summary && (
            <p className="mt-1 max-w-prose-narrow text-slate-600">
              {data.procedural_anchor.summary}
            </p>
          )}
        </div>
      </section>

      <section className="card mb-4 p-5">
        <h2 className="mb-2 text-lg font-medium text-slate-800">Letter</h2>
        <pre className="max-w-prose-narrow whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
          {data.narrative}
        </pre>
      </section>

      {data.cross_check_findings.length > 0 && (
        <section className="card mb-4 p-5">
          <h2 className="mb-2 text-lg font-medium text-slate-800">
            Point-by-point rebuttal ({data.cross_check_findings.length})
          </h2>
          <ul role="list" className="space-y-3">
            {data.cross_check_findings.map((f, i) => (
              <li key={i} className="border-l-2 border-brand-300 pl-3 text-sm">
                <p className="text-slate-700">
                  <span className="font-medium text-red-700">Carrier asserts:</span> {f.claim_text}
                </p>
                <p className="mt-1 text-slate-700">
                  <span className="font-medium text-green-700">Record contradicts:</span>{" "}
                  {f.contradicting_excerpt}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  denial doc #{f.source_document_id} · record doc #{f.record_document_id}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="card mb-4 p-5">
        <h2 className="mb-2 text-lg font-medium text-slate-800">Requested relief</h2>
        <p className="max-w-prose-narrow text-sm text-slate-700">{data.requested_relief}</p>
      </section>

      {/* The three §4.2.3 source layers, clearly distinguished. */}
      <section className="card p-5">
        <h2 className="mb-2 text-lg font-medium text-slate-800">Sources</h2>
        <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-3">
          <div>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Carrier policy
            </h3>
            <ul role="list" className="space-y-1">
              {carrier.available && carrier.policies.length > 0 ? (
                carrier.policies.map((p, i) => (
                  <li key={i}>
                    <span className="font-medium">{p.policy_number}</span> — {p.title}
                  </li>
                ))
              ) : (
                <li className="text-slate-500">Not loaded for this carrier.</li>
              )}
            </ul>
          </div>
          <div>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Procedural (Tex. Ins. Code)
            </h3>
            <ul role="list" className="space-y-1">
              {data.sources.statutes.map((s, i) => (
                <li key={i}>
                  <span className="font-medium">{s.citation}</span> — {s.title}
                </li>
              ))}
              {data.sources.statutes.length === 0 && (
                <li className="text-slate-500">None cited.</li>
              )}
            </ul>
          </div>
          <div>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Records
            </h3>
            {data.sources.attending_note && (
              <p className="mb-1 text-xs text-slate-600">
                <span className="font-semibold text-slate-700">Attending note:</span>{" "}
                <Link
                  to={`/documents/${data.sources.attending_note.document_id}`}
                  className="text-brand-700 hover:underline"
                >
                  #{data.sources.attending_note.document_id}{" "}
                  {data.sources.attending_note.filename}
                </Link>
              </p>
            )}
            <ul role="list" className="space-y-1">
              {data.sources.documents.map((s) => (
                <li key={s.document_id}>
                  <Link to={`/documents/${s.document_id}`} className="text-brand-700 hover:underline">
                    #{s.document_id} {s.filename}
                  </Link>
                </li>
              ))}
              {data.sources.documents.length === 0 && (
                <li className="text-slate-500">No records on file.</li>
              )}
            </ul>
          </div>
        </div>
      </section>

      <CorpusVersionFooter
        corpusVersion={data.corpus_version}
        corpusAppliedAt={data.corpus_applied_at}
        isStale={data.is_stale}
      />

      <p className="mt-4 max-w-prose-narrow text-xs text-slate-400">
        This tool does not provide medical or legal advice. Final clinical judgment rests with the
        treating provider, and procedural rights vary by plan type.
      </p>
    </div>
  );
}

function CorpusVersionFooter({
  corpusVersion,
  corpusAppliedAt,
  isStale,
}: {
  corpusVersion: string | null | undefined;
  corpusAppliedAt: string | null | undefined;
  isStale: boolean | undefined;
}): JSX.Element {
  if (!corpusVersion) {
    return (
      <footer className="mt-6 text-xs text-slate-500">
        Citations verified against corpus: (no corpus version pinned).
      </footer>
    );
  }
  let days: number | null = null;
  if (corpusAppliedAt) {
    days = Math.floor((Date.now() - new Date(corpusAppliedAt).getTime()) / (1000 * 60 * 60 * 24));
  }
  return (
    <footer className={`mt-6 text-xs ${isStale ? "text-amber-700" : "text-slate-500"}`}>
      Citations verified against corpus <span className="font-mono">{corpusVersion}</span>
      {days !== null && (
        <>
          {" "}
          (refreshed {days} day{days === 1 ? "" : "s"} ago)
        </>
      )}
      {isStale && (
        <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 font-medium text-amber-800">
          Stale — over 180 days old
        </span>
      )}
    </footer>
  );
}
